import type { QueryClient } from '@tanstack/react-query';

import { API_SYNC_DELAY_MS } from '@/config/constants';
import { broadcastDashboardMutation } from '@/features/dashboard/api/useDashboardSync';
import type { DashboardTab } from '@/features/dashboard/types';
import { http } from '@/services/http';

import { dashboardOptions } from '../dashboard.options';
import { getDashboardApiUrl } from '../dashboardUrl';
import { isRenameOperation } from './dashboardMutation.utils';

export const waitForApiSync = (): Promise<void> =>
  new Promise<void>(resolve => {
    setTimeout(resolve, API_SYNC_DELAY_MS);
  });

export const saveDashboardToApi = async (
  dashboardName: string,
  payload: DashboardTab,
  oldDashboardName?: string
): Promise<void> => {
  if (oldDashboardName && isRenameOperation(oldDashboardName, dashboardName)) {
    await http.internal.patch(getDashboardApiUrl(encodeURIComponent(oldDashboardName)), payload, {
      params: { newKey: dashboardName }
    });
  } else {
    await http.internal.post(getDashboardApiUrl(encodeURIComponent(dashboardName)), payload);
  }
  await waitForApiSync();
};

export const saveDashboardsBulkToApi = async (
  fullStructure: Record<string, DashboardTab>
): Promise<void> => {
  await http.internal.post(getDashboardApiUrl(), fullStructure);
  await waitForApiSync();
};

export const deleteDashboardFromApi = async (dashboardName: string): Promise<void> => {
  await http.internal.delete(getDashboardApiUrl(encodeURIComponent(dashboardName)));
  await waitForApiSync();
};

export const invalidateAndBroadcast = async (queryClient: QueryClient): Promise<void> => {
  try {
    await queryClient.invalidateQueries(
      { queryKey: dashboardOptions.all(), refetchType: 'active' },
      { throwOnError: true }
    );
  } finally {
    broadcastDashboardMutation();
  }
};
